/*
  ColorPicker.jsx
*/

// Npm.
import React, { useMemo } from 'react';

// Hooks.
import useId from '../../hooks/useId';

// Makes.
import { makeStyles, cx } from '../../makes/makeStyles';

// Constants.
import { DEFAULT_NODE_COLOR } from '../../utils/constants';

// Local constants.
const NOOP = () => {};

// Options.
const DEFAULT_OPTIONS = [
  '#06B4E1',
  '#FFB255',
  '#3CD009',
  '#FB6375',
  '#E5EF73',
  '#EB7FEF',
  '#7038E1',
  DEFAULT_NODE_COLOR,
];

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    color: theme.color_text_primary,
    '& > label': {
      fontSize: '0.8rem',
      fontWeight: 500,
    },
    '&.disabled > label': {
      color: theme.color_text_disabled,
    },
    '& > .inputs': {
      display: 'flex',
      flexWrap: 'wrap',
      margin: theme.space(-0.5, 0, 0, -0.5),
      '& > div': {
        padding: theme.space(0.5, 0, 0, 0.5),
        '& > div': {
          position: 'relative',
          lineHeight: 0,
          '& > input': {
            position: 'absolute',
            opacity: 0,
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            margin: 0,
            padding: 0,
            zIndex: 1,
            cursor: 'pointer',
            border: 'none',
            '&:hover + .circle': {
              opacity: 1,
            },
            '&:checked + .circle': {
              opacity: 1,
              backgroundColor: 'currentColor',
            },
            '&:disabled + .circle': {
              opacity: 0.6,
            },
            '&:disabled': {
              cursor: 'inherit',
            },
            '&:read-only': {
              cursor: 'inherit',
            },
          },
          '& > .circle': {
            display: 'inline-block',
            width: 22,
            height: 22,
            borderRadius: '50%',
            border: '2px solid currentColor',
            opacity: 0.6,
          },
        }
      }
    },
  },
}));

/**
 * Color picker component.
 */
export default function ColorPicker({
  className,
  label,
  options,
  value,
  onChange,
  disabled = false,
  readOnly = false,
  ...rest
}) {

  options = options || DEFAULT_OPTIONS;

  const id = useId();
  const inputName = `${id}-ColorPicker`;

  let rootClass = 'ColorPicker';

  if (disabled) {
    rootClass += ' disabled';
  }

  const classes = useStyles();
  const cls = useMemo(() => cx(rootClass, classes.root, className), [rootClass, classes.root, className]);

  return (
    <div className={cls} {...rest}>
      {
        Boolean(label) &&
        <label>{ label }</label>
      }
      <div className="inputs" role="radiogroup">
        {
          options.map(color =>
            <div key={color}>
              <div>
                <input
                  type="radio"
                  title={color}
                  name={inputName}
                  value={color}
                  checked={color === value}
                  onChange={readOnly ? NOOP : onChange}
                  readOnly={readOnly}
                  disabled={disabled}
                  style={{ color }}
                />
                <div
                  className="circle"
                  style={{ color }}
                />
              </div>
            </div>
          )
        }
      </div>
    </div>
  );
}
